import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { piiTitle } from 'src/components/DemoBlur';
import { Iconify } from '../../../../../components';
import CallButton from '../../../../../components/outbound/CallButton';
import { getLastContactChannelMeta } from '../../../../../utils/lastContact';

// "Combine contact" (mirrors "Combine name"): icon-pair cell that replaces
// the separate Email/Phone columns when the switch is on. Row fields come
// from useLeadTableHelper.js (email: lead_contact.email, phone:
// lead_contact.phone).
//
// Deliberately a DUMB cell with no local popover state: clicking either icon
// calls `onOpen(e, row)`, wired by NewLeadDataGrid.jsx to a PARENT-level
// anchor/row state that renders ONE shared <ContactDetailsPopover> (see that
// file). A local useState here looked fine in isolation but did not open
// live — DataGridPro re-renders/recreates cell subtrees often enough that
// per-cell component state is unreliable, which is exactly why every OTHER
// in-cell popover/menu in this grid already lifts its state to the parent
// (openMenu/setOpenMenuActions for the row action menu, overflowPopoverState
// for the "+N more" checklist overflow popover — see NewLeadDataGrid.jsx).
// This mirrors that established pattern instead of re-introducing the bug.
const ContactDetailsCellContent = ({ row, onOpen = undefined }) => {
    const email = row?.email || '';
    const phone = row?.phone || '';

    if (!email && !phone) {
        return <Typography variant='body2' color='text.secondary'>—</Typography>;
    }

    const handleClick = (e) => {
        e.stopPropagation();
        onOpen?.(e, row);
    };

    // Reuse the same channel icon/colour map as the "Last contacted" column
    // (LastContactedField.jsx) and MobileLeadCard.jsx, rather than hardcoding
    // our own eva:* icon names here.
    const emailMeta = getLastContactChannelMeta('EMAIL');
    const phoneMeta = getLastContactChannelMeta('PHONE');

    return (
        <Box
            data-testid='contact-details-cell'
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}
        >
            <Tooltip title={piiTitle(email) || 'No email'}>
                <span>
                    <IconButton size='small' onClick={handleClick} aria-label='Open contact details'>
                        <Iconify
                            icon={emailMeta.icon}
                            width={18}
                            height={18}
                            sx={{
                                color: email ? 'text.secondary' : 'text.disabled',
                                '&:hover': email ? { color: emailMeta.color } : undefined,
                            }}
                        />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={piiTitle(phone) || 'No phone'}>
                <span>
                    <IconButton size='small' onClick={handleClick} aria-label='Open contact details'>
                        <Iconify
                            icon={phoneMeta.icon}
                            width={18}
                            height={18}
                            sx={{
                                color: phone ? 'text.secondary' : 'text.disabled',
                                '&:hover': phone ? { color: phoneMeta.color } : undefined,
                            }}
                        />
                    </IconButton>
                </span>
            </Tooltip>
            {/* Opens the one dialer pre-filled with this lead (CU-869fmbgfj). */}
            <CallButton to={phone} leadId={row?.lead_id || null} sx={{ p: 0.5 }} />
        </Box>
    );
};

ContactDetailsCellContent.propTypes = {
    row: PropTypes.shape({
        email: PropTypes.string,
        phone: PropTypes.string,
        lead_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    onOpen: PropTypes.func,
};

const ContactDetailsCell = ({ onOpen } = {}) => ({
    field: 'contact_details',
    headerName: 'Contact',
    width: 124,
    minWidth: 96,
    editable: false,
    sortable: false,
    // Keeps quick-filter / search-in-grid matching both email and phone even
    // though they're no longer separate columns.
    valueGetter: (params) => `${params?.row?.email || ''} ${params?.row?.phone || ''}`.trim(),
    renderCell: (params) => <ContactDetailsCellContent row={params?.row || {}} onOpen={onOpen} />,
});

export default ContactDetailsCell;
